import {
    Button,
    Typography,
    IconButton,
    Box,
    Grid2,
    Icon,
    Paper
} from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { getFormatedDate } from "../../../_helpers/dateTime.helper";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { caseSubjectService } from '../../../_services/caseSubject.service';
import EnumSelect from "../../../_components/autocomplete/enum.select";
import { CountryEnum } from "../../../_common/enum/country.enum";
import { accountService } from "../../../_services/accounts.service";
import { StandardList } from "../../../_components/list/standardList.component";
import { StandardListItem } from "../../../_components/list/standardListItem.component";

export function CaseSubjectManage() {

    const resetData = () => ({
        name: '',
        address: {
            country: CountryEnum.Ireland,
        },
    });

    const { id } = useParams<{ id: string }>();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [subjects, setSubjects] = useState<any[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showForm, setShowForm] = useState(false);

    const isAdmin = accountService.isAdmin || accountService.isUser;

    useEffect(() => {
        if (id != null) fetchData();
        else setData(resetData());
    }, [id]);

    function fetchData() {
        if (!id) return;
        setIsLoading(true);
        return caseSubjectService
            .getAll(id)
            .then(
                (json) => {
                    setSubjects(json);
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        const _data = { ...data, caseId: id };

        return caseSubjectService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    setEditingIndex(null);
                    setData(resetData());
                    setShowForm(false);
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable(id: string) {
        if (!id) return;
        setIsLoading(true);
        return caseSubjectService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    setData(resetData());
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    const edit = (index: number) => {
        const subject = subjects[index];
        setData(subject);
        setEditingIndex(index);
        setShowForm(true);
    };

    const addNew = () => {
        setData(resetData());
        setEditingIndex(null);
        setShowForm(true);
    };

    const cancel = () => {
        setShowForm(false);
        setData(resetData());
        setEditingIndex(null);
    };

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    const handleAddressChange = (event: any) => {
        setData((_data: any) => ({
            ..._data, address:
            {
                ..._data.address, [event.target.name]: event.target.value
            }
        }));
    };

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6"></Typography>
                {!showForm && isAdmin && (
                    <Button variant="contained" onClick={addNew}>
                        Add Subject
                    </Button>
                )}
            </Box>

            {showForm && (
                <ValidatorForm onSubmit={submit} noValidate>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Grid2 container spacing={1.5}>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        name="name"
                                        label="Name"
                                        required
                                        fullWidth
                                        validators={['required', 'minStringLength:1']}
                                        errorMessages={['required field', "Field cannot be empty"]}
                                        value={data.name}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="dateOfBirth"
                                        name="dateOfBirth"
                                        label="Date Of Birth"
                                        type="date"
                                        fullWidth
                                        value={data.dateOfBirth != null ? getFormatedDate(data.dateOfBirth) : ""}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        name="phone"
                                        label="Phone"
                                        fullWidth
                                        validators={['matchRegexp:^\\+?[0-9]{7,15}$']}
                                        errorMessages={['Enter a valid phone number (7-15 digits, optional +)']}
                                        value={data.phone}
                                        onChange={handleChange}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="email"
                                        name="email"
                                        label="Email"
                                        fullWidth
                                        validators={['isEmail']}
                                        errorMessages={['Enter a valid email address']}
                                        value={data.email}
                                        onChange={handleChange}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="occupation"
                                        name="occupation"
                                        label="Occupation"
                                        fullWidth
                                        value={data.occupation}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="employment"
                                        name="employment"
                                        label="Employment"
                                        fullWidth
                                        value={data.employment}
                                        onChange={handleChange}
                                    />
                                </Grid2>
                            </Grid2>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Grid2 container spacing={1.5}>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="addressLine1"
                                        name="addressLine1"
                                        label="Address Line 1"
                                        fullWidth
                                        value={data?.address?.addressLine1}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="addressLine2"
                                        name="addressLine2"
                                        label="Address Line 2"
                                        fullWidth
                                        value={data?.address?.addressLine2}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="city"
                                        name="city"
                                        label="Town/City"
                                        fullWidth
                                        value={data?.address?.city}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="eirCode"
                                        name="eirCode"
                                        label="EirCode/PostCode"
                                        fullWidth
                                        value={data?.address?.eirCode}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <EnumSelect
                                        enumObject={CountryEnum}
                                        label="Select Country"
                                        name="country"
                                        value={data?.address?.country}
                                        onChange={handleAddressChange}
                                        required />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="originallyFrom"
                                        name="originallyFrom"
                                        label="Originally From"
                                        fullWidth
                                        value={data.originallyFrom}
                                        onChange={handleChange}
                                    />
                                </Grid2>
                            </Grid2>
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="businessAddresses"
                                name="businessAddresses"
                                label="Business Addresses"
                                multiline
                                rows={2}
                                fullWidth
                                value={data.businessAddresses}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="other"
                                name="other"
                                label="Other"
                                multiline
                                rows={4}
                                fullWidth
                                value={data.other}
                                onChange={handleChange}
                            />
                        </Grid2>

                        {isAdmin &&
                            <Grid2 size={{ xs: 6 }}>
                                <StandardButton
                                    saving={saving}
                                    loading={isLoading}
                                    icon="save"
                                    type="submit"
                                >
                                    Save
                                </StandardButton>
                            </Grid2>
                        }
                        <Grid2 size={{ xs: isAdmin ? 6 : 12 }}>
                            <StandardButton
                                saving={saving}
                                loading={isLoading}
                                icon="block"
                                variant="error"
                                onClick={cancel}>
                                Cancel
                            </StandardButton>
                        </Grid2>

                    </Grid2>
                </ValidatorForm>
            )}

            <StandardList isLoading={isLoading}>
                {!isLoading && subjects.map((item: any, index: any) => (
                    <StandardListItem
                        primary={item.name}
                        buttons={
                            (accountService.isAdmin || accountService.isUser) ?
                                [
                                    { icon: "edit", onClick: () => edit(index), variant: "primary" },
                                    { icon: "delete", onClick: () => disable(item.id), variant: "error" },
                                ] :
                                [
                                    { icon: "visibility", onClick: () => edit(index), variant: "primary" },

                                ]
                        }
                        key={item.id}

                    />
                ))}

            </StandardList>

        </Box>
    );
}
